import React, { useState, useEffect, useMemo } from 'react';
import axios, { publicAxios } from '../utils/axiosConfig';
import { COUNTRIES_BY_REGION, GeoRegion, REGION_LABELS } from '../data/countries';
import { getCountryNameByCode } from '../utils/countryLookup';
import CountryBadge from '../components/CountryBadge';
import { useAuth } from '../contexts/AuthContext';
import SEOHead from '../components/SEOHead';
import useDocumentTitle from '../hooks/useDocumentTitle';

interface LeaderboardEntry {
  rank: number;
  userId?: string;
  username: string;
  isPremium?: boolean;
  isBot?: boolean;
  geoRegion: GeoRegion;
  country?: string | null;
  countryCode?: string | null;
  score?: number;
  completionTime?: number;
  questionsCorrect?: number;
  dateCompleted?: string;
  averageScore?: number;
  totalScore?: number;
  quizzesCompleted?: number;
  totalCorrect?: number;
  averageCompletionTime?: number;
}

interface LeaderboardData {
  leaderboard: LeaderboardEntry[];
  userRank: number | null;
  filters: LeaderboardFiltersResponse;
  pagination: any;
}

interface RegionMeta {
  id: GeoRegion;
  label: string;
  userCount: number;
  countries: { name: string; code: string }[];
}

interface LeaderboardFiltersResponse {
  region: GeoRegion;
  country?: string | null;
  countryCode?: string | null;
  topic?: number | null;
  date?: string;
  weekStart?: string;
  weekEnd?: string;
  monthStart?: string;
  monthEnd?: string;
}

const TOPIC_OPTIONS: Array<{ value: number | null; label: string }> = [
  { value: null, label: 'All Topics' },
  { value: 1, label: 'Pop Culture' },
  { value: 2, label: 'Science & Nature' },
  { value: 3, label: 'History & Politics' },
  { value: 4, label: 'Geography & World' },
  { value: 5, label: 'Sports & Games' },
];

const Leaderboard: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState<'today' | 'weekly' | 'monthly'>('today');
  // const [viewMode, setViewMode] = useState<'individual' | 'team'>('individual');
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    region: 'global' as GeoRegion,
    country: '',
    topic: null as number | null,
    limit: 50,
    offset: 0
  });
  const [regionMetadata, setRegionMetadata] = useState<RegionMeta[]>([]);
  const [loadingRegions, setLoadingRegions] = useState(true);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [resetCountdown, setResetCountdown] = useState('--:--:--');
  const [topThreePlayers, setTopThreePlayers] = useState<LeaderboardEntry[]>([]);
  useDocumentTitle('Cognidaily | Leaderboard');

  const fallbackRegions = useMemo<RegionMeta[]>(() => {
    const regionKeys = Object.keys(REGION_LABELS) as GeoRegion[];
    return regionKeys.map((id) => ({
      id,
      label: REGION_LABELS[id],
      userCount: 0,
      countries: COUNTRIES_BY_REGION[id].map((country) => ({
        name: country.name,
        code: country.code
      }))
    }));
  }, []);

  const regionOptions = regionMetadata.length ? regionMetadata : fallbackRegions;

  const availableCountries = useMemo(() => {
    const region = regionOptions.find((option) => option.id === filters.region);
    return region ? region.countries : [];
  }, [regionOptions, filters.region]);

  useEffect(() => {
    if (!filters.country) {
      return;
    }

    const matches = availableCountries.some((country) => country.code === filters.country);
    if (!matches) {
      setFilters((prev) => ({ ...prev, country: '' }));
    }
  }, [availableCountries, filters.country]);

  useEffect(() => {
    const updateResetCountdown = () => {
      const now = Date.now();
      const offsetMs = 10 * 60 * 60 * 1000;
      const msPerDay = 24 * 60 * 60 * 1000;
      const nowInTargetZone = now + offsetMs;
      const startOfDay = Math.floor(nowInTargetZone / msPerDay) * msPerDay;
      const nextMidnight = startOfDay + msPerDay;
      const timeUntilReset = Math.max(0, nextMidnight - nowInTargetZone);

      const totalSeconds = Math.floor(timeUntilReset / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const formatted = [hours, minutes, seconds]
        .map((unit) => unit.toString().padStart(2, '0'))
        .join(':');

      setResetCountdown(formatted);
    };

    updateResetCountdown();
    const interval = window.setInterval(updateResetCountdown, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const loadRegions = async () => {
      try {
        setLoadingRegions(true);
        // Use public axios instance if not authenticated to avoid sending auth headers
        const client = isAuthenticated ? axios : publicAxios;
        const response = await client.get('/api/leaderboard/regions');
        if (response.data.success) {
          setRegionMetadata(response.data.data.regions);
        }
      } catch (metaError: any) {
        console.error('Failed to load region metadata:', metaError.response?.data || metaError.message);
      } finally {
        setLoadingRegions(false);
      }
    };

    loadRegions();
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        setError('');

        const params: Record<string, unknown> = {
          region: filters.region,
          limit: filters.limit,
          offset: filters.offset
        };

        if (filters.country) {
          params.country = filters.country;
        }
        if (filters.topic != null) {
          params.topic = filters.topic;
        }

        const client = isAuthenticated ? axios : publicAxios;
        const response = await client.get(`/api/leaderboard/${activeTab}`, { params });
        
        if (response.data.success) {
          setLeaderboardData(response.data.data);
        } else {
          setError(response.data.message || 'Failed to load leaderboard');
        }
      } catch (err: any) {
        console.error('Leaderboard loading error:', err.response?.data || err.message);
        setError(`Failed to load leaderboard: ${err.response?.data?.error || err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [activeTab, filters, isAuthenticated]);

  // Fetch top 3 players separately for podium (always offset=0, limit=3)
  useEffect(() => {
    const fetchTopThree = async () => {
      try {
        const params: Record<string, unknown> = {
          region: filters.region,
          limit: 3,
          offset: 0
        };

        if (filters.country) {
          params.country = filters.country;
        }
        if (filters.topic != null) {
          params.topic = filters.topic;
        }

        const client = isAuthenticated ? axios : publicAxios;
        const response = await client.get(`/api/leaderboard/${activeTab}`, { params });
        
        if (response.data.success && response.data.data?.leaderboard) {
          setTopThreePlayers(response.data.data.leaderboard.slice(0, 3));
        } else {
          setTopThreePlayers([]);
        }
      } catch (err: any) {
        console.error('Failed to load top 3 players:', err.response?.data || err.message);
        setTopThreePlayers([]);
      }
    };

    fetchTopThree();
  }, [activeTab, filters.region, filters.country, filters.topic, isAuthenticated]);

  const handleRegionChange = (region: GeoRegion) => {
    setFilters(prev => ({
      ...prev,
      region,
      country: '',
      offset: 0
    }));
  };

  const handleCountryChange = (countryCode: string) => {
    setFilters(prev => ({
      ...prev,
      country: countryCode,
      offset: 0
    }));
  };

  const handleTopicChange = (topic: number | null) => {
    setFilters(prev => ({
      ...prev,
      topic,
      offset: 0
    }));
  };

  const handlePageChange = (newOffset: number) => {
    setFilters(prev => ({ ...prev, offset: newOffset }));
  };

  const formatTime = (milliseconds?: number) => {
    if (!milliseconds || milliseconds <= 0) {
      return '-';
    }
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getRegionLabel = (region: GeoRegion) => REGION_LABELS[region] || region;

  const renderUsernameCell = (entry: LeaderboardEntry) => {
    const content = <>{entry.username}{entry.isPremium ? ' ★' : ''}</>;
    return <span title={entry.isBot ? 'Bot' : undefined}>{content}</span>;
  };
  const renderPodiumUsername = (entry: LeaderboardEntry) => (
    <span title={entry.isBot ? 'Bot' : undefined}>{entry.username}</span>
  );


  // topThreePlayers is now fetched separately in useEffect above
  // This ensures it always shows the actual top 3 for current filters, not current page

  // Calculate progress for the current period
  // This must be called before any early returns to follow React Hooks rules
  const progressData = useMemo(() => {
    if (!leaderboardData?.filters) {
      return null;
    }

    const now = new Date();
    const offsetMs = 10 * 60 * 60 * 1000; // GMT+10 offset
    const nowInTargetZone = new Date(now.getTime() + offsetMs);
    
    if (activeTab === 'today') {
      // Calculate progress and use countdown timer for label
      const msPerDay = 24 * 60 * 60 * 1000;
      const startOfDay = Math.floor(nowInTargetZone.getTime() / msPerDay) * msPerDay;
      const nextMidnight = startOfDay + msPerDay;
      const timeUntilReset = Math.max(0, nextMidnight - nowInTargetZone.getTime());
      const totalTimeInDay = msPerDay;
      const progress = Math.max(0, Math.min(1, 1 - (timeUntilReset / totalTimeInDay)));
      
      // Use the resetCountdown state (already formatted as hh:mm:ss)
      return {
        type: 'today' as const,
        progress,
        label: `${resetCountdown} remaining`,
        dateRange: leaderboardData.filters.date || null
      };
    }

    if (activeTab === 'weekly' && leaderboardData.filters.weekStart && leaderboardData.filters.weekEnd) {
      // Parse dates (YYYY-MM-DD format) - these represent dates in GMT+10
      // To convert to UTC timestamps, we need to account for the timezone offset
      const [startYear, startMonth, startDay] = leaderboardData.filters.weekStart.split('-').map(Number);
      const [endYear, endMonth, endDay] = leaderboardData.filters.weekEnd.split('-').map(Number);
      
      // Create dates at midnight GMT+10
      // Date.UTC creates UTC midnight, but we want GMT+10 midnight, so subtract offset
      const startDateUtc = Date.UTC(startYear, startMonth - 1, startDay) - offsetMs;
      // End date is end of Sunday (last moment of the day in GMT+10)
      const endDateUtc = Date.UTC(endYear, endMonth - 1, endDay) - offsetMs + (24 * 60 * 60 * 1000) - 1;
      
      const nowUtc = now.getTime();
      
      // Calculate days remaining (handle edge case: if we're past the end, show 0)
      const msRemaining = Math.max(0, endDateUtc - nowUtc);
      const daysRemaining = Math.max(0, Math.ceil(msRemaining / (24 * 60 * 60 * 1000)));
      
      // Calculate progress (0 to 1)
      // Handle edge case: if we're at the start (Monday 00:00), progress is 0
      // If we're at or past the end, progress is 1
      const totalMs = endDateUtc - startDateUtc + 1;
      const elapsedMs = Math.max(0, Math.min(totalMs, nowUtc - startDateUtc));
      const progress = totalMs > 0 ? Math.max(0, Math.min(1, elapsedMs / totalMs)) : 0;
      
      return {
        type: 'weekly' as const,
        progress,
        label: `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`,
        dateRange: `${leaderboardData.filters.weekStart} – ${leaderboardData.filters.weekEnd}`
      };
    }

    if (activeTab === 'monthly' && leaderboardData.filters.monthStart && leaderboardData.filters.monthEnd) {
      // Parse dates (YYYY-MM-DD format) - these represent dates in GMT+10
      const [startYear, startMonth, startDay] = leaderboardData.filters.monthStart.split('-').map(Number);
      const [endYear, endMonth, endDay] = leaderboardData.filters.monthEnd.split('-').map(Number);
      
      // Create dates at midnight GMT+10
      const startDateUtc = Date.UTC(startYear, startMonth - 1, startDay) - offsetMs;
      // End date is last moment of the last day of the month in GMT+10
      const endDateUtc = Date.UTC(endYear, endMonth - 1, endDay) - offsetMs + (24 * 60 * 60 * 1000) - 1;
      
      const nowUtc = now.getTime();
      
      // Calculate days remaining (handle edge case: if we're past the end, show 0)
      const msRemaining = Math.max(0, endDateUtc - nowUtc);
      const daysRemaining = Math.max(0, Math.ceil(msRemaining / (24 * 60 * 60 * 1000)));
      
      // Calculate progress (0 to 1)
      // Handle edge case: if we're at the start (1st 00:00), progress is 0
      // If we're at or past the end, progress is 1
      const totalMs = endDateUtc - startDateUtc + 1;
      const elapsedMs = Math.max(0, Math.min(totalMs, nowUtc - startDateUtc));
      const progress = totalMs > 0 ? Math.max(0, Math.min(1, elapsedMs / totalMs)) : 0;
      
      return {
        type: 'monthly' as const,
        progress,
        label: `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`,
        dateRange: `${leaderboardData.filters.monthStart} – ${leaderboardData.filters.monthEnd}`
      };
    }

    return null;
  }, [activeTab, leaderboardData?.filters, resetCountdown]);

  const renderPagination = () => {
    if (!leaderboardData?.pagination) return null;

    const { page, totalPages } = leaderboardData.pagination;
    const pages = [];
    
    for (let i = Math.max(1, page - 2); i <= Math.min(totalPages, page + 2); i++) {
      pages.push(i);
    }

    return (
      <nav className="leaderboard-pagination" aria-label="Leaderboard pagination">
        <button type="button" className="btn btn-secondary btn-compact" onClick={() => handlePageChange((page - 2) * filters.limit)} disabled={page <= 1}>Previous</button>
        {pages.map((pageNum) => (
          <button key={pageNum} type="button" className={`btn btn-secondary btn-compact ${pageNum === page ? 'leaderboard-pagination__current' : ''}`} onClick={() => handlePageChange((pageNum - 1) * filters.limit)}>
            {pageNum}
          </button>
        ))}
        <button type="button" className="btn btn-secondary btn-compact" onClick={() => handlePageChange(page * filters.limit)} disabled={page >= totalPages}>Next</button>
      </nav>
    );
  };

  if (loading) {
    return <section className="section"><p>Loading leaderboard...</p></section>;
  }

  const activeFilters = leaderboardData?.filters;
  const activeRegion = (activeFilters?.region ?? filters.region) as GeoRegion;
  const reportedCountry = activeFilters?.country ?? '';
  const activeCountryCode =
    activeFilters?.countryCode ??
    (reportedCountry && reportedCountry.length <= 3 ? reportedCountry : undefined) ??
    (filters.country || undefined);
  const derivedCountryName =
    (reportedCountry && reportedCountry.length > 3 ? reportedCountry : undefined) ??
    getCountryNameByCode(activeCountryCode);
  const regionLabelText = getRegionLabel(activeRegion);

  const userLocationContent = activeCountryCode || derivedCountryName
    ? (
        <>
          <CountryBadge
            countryCode={activeCountryCode}
            countryName={derivedCountryName}
            fallback={derivedCountryName || activeCountryCode}
          />
          {' / '}
          {regionLabelText}
        </>
      )
    : regionLabelText;


  // Map-related data prepared for future map implementation
  // const topTenEntries = leaderboardData?.leaderboard?.slice(0, 10) ?? [];
  // const heatmapTitle = (() => {
  //   if (dateLabelContent) {
  //     if (activeTab === 'today') {
  //       return `Heatmap for ${dateLabelContent.replace('Showing results for ', '')}`;
  //     }
  //     return `Heatmap for ${dateLabelContent}`;
  //   }
  //   if (activeTab === 'weekly') {
  //     return 'Heatmap for This Week';
  //   }
  //   if (activeTab === 'monthly') {
  //     return 'Heatmap for This Month';
  //   }
  //   return 'Heatmap for Today';
  // })();

  const renderPodium = () => {
    if (topThreePlayers.length === 0) {
      return <p className="leaderboard-page__empty">No players yet.</p>;
    }
    const [first, second, third] = [
      topThreePlayers[0] || null,
      topThreePlayers[1] || null,
      topThreePlayers[2] || null
    ];
    return (
      <section className="leaderboard-podium">
        <div className="leaderboard-podium__slot leaderboard-podium__slot--2">
          <span className="leaderboard-podium__rank">2</span>
          <span className="leaderboard-podium__name">{second ? renderPodiumUsername(second) : '–'}</span>
          <span className="leaderboard-podium__score">{second ? `${second.totalScore ?? 0} pts` : '–'}</span>
        </div>
        <div className="leaderboard-podium__slot leaderboard-podium__slot--1">
          <span className="leaderboard-podium__rank">1</span>
          <span className="leaderboard-podium__name">{first ? renderPodiumUsername(first) : '–'}</span>
          <span className="leaderboard-podium__score">{first ? `${first.totalScore ?? 0} pts` : '–'}</span>
        </div>
        <div className="leaderboard-podium__slot leaderboard-podium__slot--3">
          <span className="leaderboard-podium__rank">3</span>
          <span className="leaderboard-podium__name">{third ? renderPodiumUsername(third) : '–'}</span>
          <span className="leaderboard-podium__score">{third ? `${third.totalScore ?? 0} pts` : '–'}</span>
        </div>
      </section>
    );
  };

  const renderLeaderboardEntries = () => {
    if (!leaderboardData?.leaderboard) return null;
    const showQuizzesColumn = true; /* Always show for consistent table width */

    return (
      <>
        <div className="leaderboard-cards">
          {leaderboardData.leaderboard.map((entry) => {
            const rowKey = `${entry.username}-${entry.rank}`;
            const metaParts = [
              `${entry.totalCorrect ?? 0} correct`,
              ...(showQuizzesColumn ? [`${entry.quizzesCompleted ?? 0} quizzes`] : []),
              formatTime(entry.averageCompletionTime)
            ];
            return (
              <div key={rowKey} className="leaderboard-card">
                <div className="leaderboard-card__main">
                  <span className="leaderboard-card__rank">#{entry.rank}</span>
                  <span className="leaderboard-card__user">{renderUsernameCell(entry)}</span>
                  <span className="leaderboard-card__score">{entry.totalScore ?? 0} pts</span>
                  <span className="leaderboard-card__meta">
                    {metaParts.join(' · ')}
                    {' · '}
                    {getRegionLabel(entry.geoRegion)}
                    {(entry.country || entry.countryCode) && (
                      <>
                        {' '}
                        <CountryBadge
                          countryCode={entry.countryCode}
                          countryName={entry.country}
                          className="leaderboard-card__flag"
                          fallback={null}
                        />
                      </>
                    )}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        <div className="leaderboard-table-wrap">
          <table className="leaderboard-table">
            <colgroup>
              <col /><col /><col /><col /><col /><col /><col /><col />
            </colgroup>
            <thead>
              <tr>
                <th>Rank</th>
                <th>Username</th>
                <th>Score</th>
                <th>Correct</th>
                {showQuizzesColumn && <th>Quizzes</th>}
                <th>Time</th>
                <th>Region</th>
                <th>Country</th>
              </tr>
            </thead>
            <tbody>
              {leaderboardData.leaderboard.map((entry) => {
                const rowKey = `${entry.username}-${entry.rank}`;
                return (
                  <tr key={rowKey}>
                    <td>{entry.rank}</td>
                    <td>{renderUsernameCell(entry)}</td>
                    <td>{entry.totalScore ?? 0}</td>
                    <td>{entry.totalCorrect ?? 0}</td>
                    {showQuizzesColumn && <td>{entry.quizzesCompleted ?? 0}</td>}
                    <td>{formatTime(entry.averageCompletionTime)}</td>
                    <td>{getRegionLabel(entry.geoRegion)}</td>
                    <td>
                      <CountryBadge
                        countryCode={entry.countryCode}
                        countryName={entry.country}
                        fallback="–"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  return (
    <>
      <SEOHead
        title="Leaderboard"
        description="Daily, weekly, monthly rankings. Global and regional."
        keywords="leaderboard, rankings, trivia"
        url="https://cognidaily.io/leaderboard"
      />
      <article className="leaderboard-page">
        <header className="leaderboard-page__header">
          <h1 className="leaderboard-page__title">Leaderboard</h1>
        </header>

        <section className="leaderboard-filters card">
          {progressData && (
            <p className="countdown-badge leaderboard-filters__countdown">
              <span className="countdown-badge__label">{progressData.label}</span>
              {progressData.dateRange && <span className="countdown-badge__value"> ({progressData.dateRange})</span>}
            </p>
          )}
          <div className="leaderboard-filters__tabs">
            <button
              type="button"
              className={`leaderboard-filters__tab ${activeTab === 'today' ? 'leaderboard-filters__tab--active' : ''}`}
              onClick={() => setActiveTab('today')}
            >
              Today
            </button>
            <button
              type="button"
              className={`leaderboard-filters__tab ${activeTab === 'weekly' ? 'leaderboard-filters__tab--active' : ''}`}
              onClick={() => setActiveTab('weekly')}
            >
              Week
            </button>
            <button
              type="button"
              className={`leaderboard-filters__tab ${activeTab === 'monthly' ? 'leaderboard-filters__tab--active' : ''}`}
              onClick={() => setActiveTab('monthly')}
            >
              Month
            </button>
          </div>
          <button
            type="button"
            className="leaderboard-filters__toggle"
            onClick={() => setFiltersExpanded(!filtersExpanded)}
          >
            Filters {filtersExpanded ? '▼' : '▶'}
          </button>
          {filtersExpanded && (
            <div className="leaderboard-filters__panel">
              <div className="leaderboard-filters__row">
                <label htmlFor="filter-region" className="leaderboard-filters__label">Region</label>
                <select id="filter-region" value={filters.region} onChange={(e) => handleRegionChange(e.target.value as GeoRegion)} disabled={loadingRegions}>
                  {regionOptions.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
              <div className="leaderboard-filters__row">
                <label htmlFor="filter-country" className="leaderboard-filters__label">Country</label>
                <select id="filter-country" value={filters.country} onChange={(e) => handleCountryChange(e.target.value)} disabled={loadingRegions || availableCountries.length === 0}>
                  <option value="">All</option>
                  {availableCountries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </div>
              <div className="leaderboard-filters__row">
                <label htmlFor="filter-topic" className="leaderboard-filters__label">Topic</label>
                <select id="filter-topic" value={filters.topic ?? ''} onChange={(e) => handleTopicChange(e.target.value === '' ? null : parseInt(e.target.value, 10))}>
                  {TOPIC_OPTIONS.map((opt) => <option key={opt.value ?? 'all'} value={opt.value ?? ''}>{opt.label}</option>)}
                </select>
              </div>
            </div>
          )}
        </section>

        {error && <p role="alert" className="leaderboard-page__error text-error">{error}</p>}

        {renderPodium()}

        {leaderboardData?.userRank != null && (
          <div className="leaderboard-user-rank card">
            Your rank: <strong>#{leaderboardData.userRank}</strong> in the {userLocationContent} board
          </div>
        )}

        {renderLeaderboardEntries()}

        {renderPagination()}

        {leaderboardData && leaderboardData.leaderboard.length === 0 && (
          <p className="leaderboard-page__empty">No results match the current filters.</p>
        )}
      </article>
    </>
  );
};

export default Leaderboard;


